import React from 'react';

import ApprovalHistoryTable from './ApprovalHistoryTable';
import ApprovalStepper from './ApprovalStepper';
import BalanceImpactCallout from './BalanceImpactCallout';
import ChangeAmountSummary from './ChangeAmountSummary';
import { localDate, statusChipClass, STATUS_APPLIED, TYPE_CONTINGENCY_ALLOCATION } from './changeRequestFormat';
import PositionChanges from './PositionChanges';
import RequesterExplanation from './RequesterExplanation';
import SourceOfFundsBar from './SourceOfFundsBar';

import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';

/**
 * The request details pop-up. Read-only: approvals are actioned from the approver's
 * home page, so the only action here is Close.
 *
 * It is handed the row object it was opened from and fetches nothing. That is what
 * makes the table and the pop-up structurally unable to disagree about a figure —
 * the defect that showed Local 2,500 on the table and 0 here for the same request.
 */
export default function ChangeRequestModal({ modal, row, profitEnabled = false }) {
    const close = () => modal.hide();

    if (!row) {
        return <Modal modal={modal} parentContainerClasses="tw-max-w-3xl" />;
    }

    const funding = row.funding || {};
    const applied = row.status === STATUS_APPLIED;
    // Shown in whole units, so a change that rounds away moved no money to source. An
    // allocation sets a pot rather than drawing on one, so it has no source either.
    const movedMoney = Math.round(Number(row.total_amount ?? 0)) !== 0 && row.type !== TYPE_CONTINGENCY_ALLOCATION;

    return (
        <Modal modal={modal} parentContainerClasses="tw-max-w-3xl">
            <ModalHeader
                dark
                close={close}
                subtitle={$t('Request {{reference}} · {{location}} · Requested by {{user}}, {{date}}', {
                    reference: row.reference,
                    location: row.location_label,
                    user: row.requested_by,
                    date: localDate(row.submitted_at),
                })}
            >
                {/* No wrap on the row itself: a long change name wraps INSIDE the title
                    so the status chip stays pinned beside it rather than dropping below. */}
                <span className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-min-w-0">
                        {row.change_label} — {row.description}
                    </span>
                    <span className={`${statusChipClass(row.status)} tw-mt-2 tw-shrink-0`}>{row.approval?.chain_position_label}</span>
                </span>
            </ModalHeader>

            <div className="modal__form-container">
                <ChangeAmountSummary row={row} />

                {movedMoney && (
                    <div className="tw-mb-4">
                        <div className="bc-eyebrow">{$t('Source of funds')}</div>
                        <SourceOfFundsBar funding={funding} category={row.category} profitEnabled={profitEnabled} />
                    </div>
                )}

                {/* Read-only, so no negative-balance warning here — that is
                    what an approver is about to do, and this screen has
                    nothing for them to do. Shared with the approval pop-up
                    (BalanceImpactCallout) so the two cannot report different
                    money for one request. */}
                <BalanceImpactCallout funding={funding} />

                <div className="bc-kv">
                    <div className="bc-kv__cell">
                        <span className="bc-kv__key">{$t('Group')}</span>
                        {row.group_label}
                    </div>
                    <div className="bc-kv__cell">
                        <span className="bc-kv__key">{$t('Type')}</span>
                        {row.type_label}
                    </div>
                    <div className="bc-kv__cell">
                        <span className="bc-kv__key">{$t('Department / cost centre')}</span>
                        {row.location_url ? (
                            <a className="bc-link" href={row.location_url}>
                                {row.location_label}
                            </a>
                        ) : (
                            row.location_label
                        )}
                    </div>
                    <div className="bc-kv__cell">
                        <span className="bc-kv__key">{$t('Reason')}</span>
                        {row.reason}
                    </div>
                </div>

                {row.field_changes?.length > 0 && (
                    <div className="tw-mb-4">
                        <PositionChanges row={row} />
                    </div>
                )}

                <div className="tw-mb-4">
                    <RequesterExplanation row={row} />
                </div>

                {/* A change applied with no approver went through no approval, so the
                    explanation above is its whole record of who made it and when. */}
                {!applied && (
                    <>
                        <div className="tw-mb-4">
                            <div className="bc-eyebrow">{$t('Approval progress')}</div>
                            <ApprovalStepper approval={row.approval} />
                        </div>

                        <ApprovalHistoryTable history={row.history} />
                    </>
                )}
            </div>

            <ModalFooter
                allowSubmit={false}
                onCancel={close}
                cancelButtonText={$t('Close')}
                startContent={applied ? null : <span className="bc-modal-note">{$t("Approvals are actioned from the approver's home page.")}</span>}
            />
        </Modal>
    );
}
